import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BadgeIndianRupee,
  Boxes,
  ChevronRight,
  PackageCheck,
  Scale,
  ShoppingCart,
  Tags,
  Users,
  Warehouse,
  X,
} from "lucide-react";
import { ReportsService } from "../../../services/reports.service";
import { ReportCard, ReportPanel } from "./components/ReportCards";
import DataTable from "./components/DataTable";
import TrendBars from "./components/TrendBars";
import { money, n } from "./components/reportFormat";

function Metric({ label, value, moneyValue = false }) {
  return (
    <div className="rounded-xl bg-[#fff7f8] px-3 py-2">
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 text-[13px] font-black text-slate-800">{moneyValue ? money(value) : n(value)}</div>
    </div>
  );
}

function weightKg(value) {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "-";
  return `${amount.toLocaleString("en-IN", { maximumFractionDigits: 3 })} kg`;
}

function ProductVariantBreakdown({ rows = [] }) {
  const displayRows = rows.map((row) => {
    const hasWeight = Number(row?.weight_value || 0) > 0 && String(row?.weight_unit_symbol || "").trim();
    const totalWeight = Number(row?.total_weight_kg || 0);

    return {
      ...row,
      variant: row?.variant_label || row?.pack_label || row?.variant_name || "Default",
      unit_weight: hasWeight ? `${n(row.weight_value)} ${row.weight_unit_symbol}` : "-",
      weight_sold: totalWeight > 0 ? weightKg(totalWeight) : "-",
    };
  });

  return (
    <DataTable
      rows={displayRows}
      columns={["variant", "unit_weight", "sold_qty", "weight_sold", "sales_amount", "order_count", "unique_customers"]}
      maxHeight="max-h-[360px]"
    />
  );
}

function CategoryCard({ row, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(row)}
      className="group w-full rounded-[18px] border border-[#f4cdd2] bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#df7a88] hover:shadow-md"
    >
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl border border-[#f7d8dc] bg-[#fff7f8]">
          {row.image_url ? <img src={row.image_url} alt="" className="h-full w-full object-cover" /> : <Boxes className="m-3 text-[#d86a78]" size={22} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-[14px] font-black text-slate-900">{row.name}</p>
            <ChevronRight size={17} className="shrink-0 text-[#d86a78] transition group-hover:translate-x-0.5" />
          </div>
          <p className="mt-1 text-[11px] font-semibold text-slate-400">{n(row.catalog_products)} products in catalog</p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Metric label="Sales" value={row.sales_amount} moneyValue />
        <Metric label="Units" value={row.sold_qty} />
        <Metric label="Orders" value={row.order_count} />
        <Metric label="Customers" value={row.unique_customers} />
      </div>
    </button>
  );
}

function ProductPerformanceModal({ open, productId, filters, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !productId) return undefined;
    let alive = true;
    setLoading(true);
    setError("");
    setData(null);
    ReportsService.productPerformance(productId, filters)
      .then((res) => alive && setData(res.data || null))
      .catch((err) => alive && setError(err?.message || "Unable to load product performance"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, productId, filters]);

  if (!open) return null;
  const product = data?.product || {};
  const summary = data?.summary || {};
  const categoryPath = (data?.category?.breadcrumbs || []).map((item) => item.name).join(" → ");

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 p-4">
      <div className="max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#f8dde1] px-6 py-4">
          <div className="min-w-0">
            <h3 className="truncate text-[18px] font-black text-slate-900">{product.name || "Product Performance"}</h3>
            <p className="mt-1 truncate text-[11px] font-semibold text-slate-400">{categoryPath || product.sku || "Performance details"}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-400 hover:bg-[#fff4f6] hover:text-[#df6374]"><X size={20} /></button>
        </div>
        <div className="max-h-[calc(92vh-74px)] overflow-y-auto bg-[#fffafa] p-5">
          {loading ? <div className="py-20 text-center text-sm font-bold text-[#d86a78]">Loading performance...</div> : null}
          {error ? <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-600">{error}</div> : null}
          {!loading && !error && data ? (
            <div className="space-y-5">
              <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-7">
                <ReportCard icon={BadgeIndianRupee} title="Sales" value={money(summary.sales_amount)} />
                <ReportCard icon={PackageCheck} title="Units" value={n(summary.sold_qty)} />
                <ReportCard
                  icon={Scale}
                  title="Total Weight"
                  value={weightKg(summary.total_weight_kg)}
                  hint={Number(summary.unweighted_units || 0) > 0 ? `${n(summary.unweighted_units)} units missing weight data` : undefined}
                />
                <ReportCard icon={ShoppingCart} title="Orders" value={n(summary.order_count)} />
                <ReportCard icon={Users} title="Customers" value={n(summary.unique_customers)} />
                <ReportCard icon={Tags} title="Profit" value={money(summary.actual_profit)} />
                <ReportCard icon={Tags} title="Margin" value={`${n(summary.profit_margin_percent)}%`} />
              </div>

              <ReportPanel
                title="Variant Sales Breakdown"
                subtitle="Shows how many units sold for each pack size and the normalized total weight sold."
              >
                <ProductVariantBreakdown rows={data.variants || []} />
              </ReportPanel>

              <TrendBars rows={data.trend || []} field="sales_amount" label="Product sales trend" />
              <div className="grid gap-5 xl:grid-cols-2">
                <ReportPanel title="Warehouse Breakdown">
                  <DataTable rows={data.warehouses || []} columns={["name", "sold_qty", "sales_amount", "order_count", "unique_customers", "actual_profit"]} maxHeight="max-h-[360px]" />
                </ReportPanel>
                <ReportPanel title="Top Customers for this Product">
                  <DataTable rows={data.topCustomers || []} columns={["user_name", "order_count", "sold_qty", "sales_amount"]} maxHeight="max-h-[360px]" />
                </ReportPanel>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function CategoryReports({ filters, onWarehouseSelect }) {
  const [categoryId, setCategoryId] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [productId, setProductId] = useState("");

  const requestFilters = useMemo(() => ({ ...filters, categoryId: categoryId || undefined, page }), [filters, categoryId, page]);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setError("");
    ReportsService.catalogPerformance(requestFilters)
      .then((res) => alive && setData(res.data || null))
      .catch((err) => alive && setError(err?.message || "Unable to load category performance"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [requestFilters]);

  useEffect(() => load(), [load]);
  useEffect(() => { setPage(1); }, [categoryId, filters.period, filters.startDate, filters.endDate, filters.warehouseId]);

  const summary = data?.summary || {};
  const rows = data?.rows || [];
  const breadcrumbs = data?.scope?.breadcrumbs || [];
  const selected = data?.scope?.selectedCategory || null;
  const mode = data?.mode || "category";
  const totalPages = Math.max(1, Math.ceil(Number(data?.total || 0) / Math.max(1, Number(filters.limit || 10))));

  const title = !selected
    ? "Main Category Performance"
    : mode === "product"
      ? `Products inside ${selected.name}`
      : Number(data?.scope?.level || 0) === 1
        ? `Categories inside ${selected.name}`
        : `Subcategories inside ${selected.name}`;

  const openCategory = (row) => {
    setCategoryId(row.id);
    setPage(1);
  };

  const openBreadcrumb = (id) => {
    setCategoryId(id || "");
    setPage(1);
  };

  return (
    <div className="space-y-5">
      <div className="rounded-[18px] border border-[#f4cdd2] bg-gradient-to-r from-[#fff7f8] to-white p-4">
        <div className="flex flex-wrap items-center gap-2 text-[12px] font-black">
          <button type="button" onClick={() => openBreadcrumb("")} className={!selected ? "text-[#d86a78]" : "text-slate-500 hover:text-[#d86a78]"}>All Catalog</button>
          {breadcrumbs.map((crumb) => (
            <React.Fragment key={crumb.id}>
              <ChevronRight size={14} className="text-slate-300" />
              <button type="button" onClick={() => openBreadcrumb(crumb.id)} className={crumb.id === selected?.id ? "text-[#d86a78]" : "text-slate-500 hover:text-[#d86a78]"}>{crumb.name}</button>
            </React.Fragment>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-[19px] font-black text-slate-900">{title}</h2>
            <p className="mt-1 text-[11px] font-semibold text-slate-400">Click a row/card to drill down. Warehouse and product breakdown update with the selected level.</p>
          </div>
          {selected ? <button type="button" onClick={() => openBreadcrumb(breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2]?.id : "")} className="rounded-xl border border-[#f2c6cc] bg-white px-3 py-2 text-xs font-black text-[#d86a78]">Back one level</button> : null}
        </div>
      </div>

      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(185px,1fr))]">
        <ReportCard icon={BadgeIndianRupee} title="Product Sales" value={money(summary.sales_amount)} />
        <ReportCard icon={PackageCheck} title="Units Sold" value={n(summary.sold_qty)} />
        <ReportCard icon={ShoppingCart} title="Orders" value={n(summary.order_count)} />
        <ReportCard icon={Users} title="Customers" value={n(summary.unique_customers)} />
        <ReportCard icon={Tags} title="Profit" value={money(summary.actual_profit)} />
        <ReportCard icon={Boxes} title="Sold Products" value={n(summary.sold_products)} />
      </div>

      {error ? <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-600">{error}</div> : null}
      {loading ? <div className="py-14 text-center font-bold text-[#d86a78]">Loading category performance...</div> : null}

      {!loading && !error ? (
        <>
          <ReportPanel title={title}>
            {mode === "category" ? (
              rows.length ? <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{rows.map((row) => <CategoryCard key={row.id} row={row} onClick={openCategory} />)}</div> : <div className="py-10 text-center text-sm font-bold text-slate-400">No categories found at this level.</div>
            ) : (
              <>
                <DataTable rows={rows} columns={["name", "sku", "sold_qty", "sales_amount", "order_count", "unique_customers", "actual_profit", "profit_margin_percent"]} onRowClick={(row) => setProductId(row.id)} />
                {Number(data?.total || 0) > Number(filters.limit || 10) ? (
                  <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-[#f8dde1] bg-[#fffafa] px-4 py-3">
                    <p className="text-xs font-bold text-slate-500">Page {page} of {totalPages} · {n(data?.total)} products</p>
                    <div className="flex gap-2">
                      <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg border border-[#f3c7ce] px-3 py-2 text-xs font-black text-[#d86a78] disabled:opacity-40">Prev</button>
                      <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded-lg border border-[#f3c7ce] px-3 py-2 text-xs font-black text-[#d86a78] disabled:opacity-40">Next</button>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </ReportPanel>

          <TrendBars rows={data?.trend || []} field="sales_amount" label="Sales trend for selected catalog level" />

          <div className="grid gap-5 xl:grid-cols-2">
            <ReportPanel title="Warehouse Breakdown">
              <div className="mb-3 flex items-center gap-2 text-[11px] font-bold text-slate-400"><Warehouse size={14} /> Which warehouse is selling this catalog scope</div>
              <DataTable rows={data?.warehouses || []} columns={["name", "city", "sold_qty", "sales_amount", "order_count", "unique_customers", "actual_profit"]} maxHeight="max-h-[390px]" onRowClick={onWarehouseSelect ? (row) => onWarehouseSelect(row.id) : null} />
            </ReportPanel>
            <ReportPanel title="Top Products in Selected Scope">
              <DataTable rows={data?.topProducts || []} columns={["name", "sku", "sold_qty", "sales_amount", "order_count", "actual_profit"]} maxHeight="max-h-[390px]" onRowClick={(row) => setProductId(row.id)} />
            </ReportPanel>
          </div>
        </>
      ) : null}

      <ProductPerformanceModal open={!!productId} productId={productId} filters={filters} onClose={() => setProductId("")} />
    </div>
  );
}
